import axios from 'axios';
import { getAuthToken } from './authService';
import { getApiUrl } from '../utils/config';

// Interface cho thống kê
export interface DashboardStats {
  totalUsers: number;
  totalTeams: number;
  todayActivities: number;
  newUsers: number;
}

// Interface cho thống kê hệ thống
export interface SystemStats {
  userCount: number;
  teamCount: number;
  tournamentCount: number;
  activityCount: number;
  totalDistance: number;
  totalMovingTime: number;
  totalElapsedTime: number;
  averageSpeed: number;
  averagePace: number;
  totalElevationGain: number;
  lastActivityDate: number[];
  topUser: {
    userId: number;
    fullName: string;
    avatarUrl: string;
    department: string | null;
    totalDistance: number;
    totalMovingTime: number;
    totalElapsedTime: number;
    activityCount: number;
    averagePace: number;
    averageSpeed: number;
    totalElevationGain: number;
    lastActivityDate: number[];
  };
  topTeam: {
    teamId: number;
    teamName: string;
    imageUrl: string;
    tournamentId: number;
    tournamentName: string;
    totalDistance: number;
    totalMovingTime: number;
    totalElapsedTime: number;
    activityCount: number;
    userCount: number;
    topUser: any;
    memberStats: any;
  };
  topTournament: {
    tournamentId: number;
    tournamentName: string;
    description: string;
    startDate: number[];
    endDate: number[];
    totalDistance: number | null;
    totalMovingTime: number | null;
    totalElapsedTime: number | null;
    activityCount: number | null;
    teamCount: number;
    userCount: number;
    teamStats: any;
    topTeam: any;
    topUser: any;
  };
}

// Interface cho thống kê hoạt động theo ngày
export interface DailyActivityStats {
  date: string;
  totalActivities: number;
  totalDistance: number; // mét
  totalElevation: number; // mét
  totalDuration: number; // giây
}

// Interface cho thống kê hoạt động theo tuần
export interface WeeklyActivityStats {
  weekStartDate: string;
  weekEndDate: string;
  totalActivities: number;
  totalDistance: number;
  totalElevation: number;
  totalDuration: number;
}

// Interface cho thống kê hoạt động theo tháng
export interface MonthlyActivityStats {
  month: string; // YYYY-MM
  totalActivities: number;
  totalDistance: number;
  totalElevation: number;
  totalDuration: number;
}

// Interface cho bảng xếp hạng
export interface LeaderboardEntry {
  rank: number;
  id: number;
  name: string;
  totalDistance: number;
  totalActivities: number;
  totalElevation: number;
  totalDuration: number;
  avatarUrl?: string;
  // Các trường bổ sung từ API
  firstname?: string;
  lastname?: string;
  teams?: string;
  activitiesByType?: {[key: string]: number};
  totalDistanceFormatted?: string;
  totalMovingTimeFormatted?: string;
}

// Interface cho thông tin người dùng
export interface AdminUserInfo {
  id: number;
  username: string;
  email: string;
  fullName: string;
  roles: string[];
  createdAt: string;
  lastLogin: string;
  avatarUrl?: string;
  stravaConnected: boolean;
}

// Interface trạng thái đồng bộ
export interface SyncStatus {
  lastSyncTime: string | null;
  syncInProgress: boolean;
  syncSuccess: boolean;
  errorMessage: string | null;
  timeSinceLastSync: number | null;
  activitiesLast7Days: number;
  stravaConnected: boolean;
  stravaTokenExpiresIn: string | null;
}

// Helper function để trích xuất mảng từ data
const extractArrayFromResponse = (data: any, defaultValue: any[] = []): any[] => {
  // Nếu data là mảng, trả về trực tiếp
  if (Array.isArray(data)) {
    return data;
  }
  
  // Nếu data là object, tìm thuộc tính chứa mảng
  if (data && typeof data === 'object') {
    // Kiểm tra cấu trúc lồng nhau phổ biến
    if (data.data) {
      // Trường hợp data.data là mảng
      if (Array.isArray(data.data)) {
        return data.data;
      }
      
      // Trường hợp data.data.data là mảng (lồng nhau 2 cấp)
      if (data.data.data && Array.isArray(data.data.data)) {
        return data.data.data;
      }
    }
    
    // Kiểm tra các thuộc tính phổ biến ở cấp đầu tiên
    const commonProperties = ['data', 'items', 'content', 'result', 'list', 'records'];
    for (const prop of commonProperties) {
      if (data[prop] && Array.isArray(data[prop])) {
        return data[prop];
      }
    }
    
    // Tìm kiếm sâu hơn trong các thuộc tính object của data
    for (const key in data) {
      if (data[key] && typeof data[key] === 'object') {
        // Kiểm tra nếu thuộc tính con chứa mảng
        if (Array.isArray(data[key])) {
          return data[key];
        }
        
        // Kiểm tra nếu thuộc tính con có thuộc tính "data" chứa mảng
        if (data[key].data && Array.isArray(data[key].data)) {
          return data[key].data;
        }
      }
    }
    
    // Tìm trường đầu tiên là mảng
    for (const key in data) {
      if (Array.isArray(data[key])) {
        return data[key];
      }
    }
  }
  
  // Nếu không tìm thấy mảng nào, trả về giá trị mặc định
  console.warn('Không tìm thấy mảng trong dữ liệu:', data);
  return defaultValue;
};

/**
 * Lấy thống kê tổng hợp toàn hệ thống
 */
export const getSystemStats = async (startDate: string, endDate: string): Promise<SystemStats> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin thống kê hệ thống');
    }
    
    const response = await axios.get(
      getApiUrl(`/api/stats/system?startDate=${startDate}&endDate=${endDate}`), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (response.data && response.data.result === "SUCCESS" && response.data.data) {
      return response.data.data as SystemStats;
    }
    
    throw new Error('Không thể lấy thống kê tổng hợp toàn hệ thống');
  } catch (error: any) {
    console.error('Lỗi khi lấy thống kê tổng hợp toàn hệ thống:', error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể lấy thống kê tổng hợp toàn hệ thống');
  }
};

/**
 * Lấy danh sách giải đấu
 */
export const getTournaments = async (): Promise<any[]> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    const response = await axios.get(
      getApiUrl('/api/tournaments'), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    // Tìm danh sách tournaments trong dữ liệu trả về
    const tournaments = extractArrayFromResponse(response.data, []);
    
    // Nếu không tìm thấy, thử tìm mảng trong thuộc tính "tournaments" (nếu có)
    if (response.data && response.data.tournaments && Array.isArray(response.data.tournaments)) {
      return response.data.tournaments;
    }
    
    return tournaments;
  } catch (error: any) {
    console.error('Lỗi khi lấy danh sách giải đấu:', error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể lấy danh sách giải đấu');
  }
};

/**
 * Lấy danh sách team theo tournament
 */
export const getTeamsByTournament = async (tournamentId: number): Promise<any[]> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    const response = await axios.get(
      getApiUrl(`/api/teams/tournament/${tournamentId}`), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return extractArrayFromResponse(response.data);
  } catch (error: any) {
    console.error(`Lỗi khi lấy danh sách team cho giải đấu ${tournamentId}:`, error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể lấy danh sách team');
  }
};

/**
 * Lấy danh sách thành viên trong team
 */
export const getTeamMembers = async (teamId: number): Promise<any[]> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    const response = await axios.get(
      getApiUrl(`/api/teams/${teamId}/members`), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return extractArrayFromResponse(response.data);
  } catch (error: any) {
    console.error(`Lỗi khi lấy danh sách thành viên của team ${teamId}:`, error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể lấy danh sách thành viên');
  }
};

/**
 * Lấy thống kê hoạt động theo ngày
 */
export const getDailyActivityStats = async (startDate: string, endDate: string): Promise<DailyActivityStats[]> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    const response = await axios.get(
      getApiUrl(`/api/activities/stats/daily?startDate=${startDate}&endDate=${endDate}`), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    // Xử lý cấu trúc response lồng nhau
    const rawData = response.data;
    
    // Trường hợp cấu trúc như mẫu: result -> data -> data (mảng)
    if (rawData && rawData.data && rawData.data.data && Array.isArray(rawData.data.data)) {
      // Ánh xạ dữ liệu vào interface DailyActivityStats
      return rawData.data.data.map((day: any) => ({
        date: day.date || day.activityDate || '',
        totalActivities: day.totalActivities || 0,
        totalDistance: typeof day.totalDistance === 'number' 
          ? (day.totalDistance < 100 ? day.totalDistance * 1000 : day.totalDistance) // Chuyển từ km sang m nếu nhỏ hơn 100
          : 0,
        totalElevation: day.totalElevation || 0,
        totalDuration: day.totalMovingTime || day.duration || 0
      }));
    }
    
    // Nếu không khớp với cấu trúc đặc biệt, sử dụng extractArray
    const extractedData = extractArrayFromResponse(response.data, []);
    
    // Ánh xạ dữ liệu vào interface DailyActivityStats
    return extractedData.map((day: any) => ({
      date: day.date || day.activityDate || '',
      totalActivities: day.totalActivities || 0,
      totalDistance: typeof day.totalDistance === 'number' 
        ? (day.totalDistance < 100 ? day.totalDistance * 1000 : day.totalDistance) // Chuyển từ km sang m nếu nhỏ hơn 100
        : 0,
      totalElevation: day.totalElevation || 0,
      totalDuration: day.totalMovingTime || day.duration || 0
    }));
  } catch (error: any) {
    console.error('Lỗi khi lấy thống kê hoạt động theo ngày:', error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể lấy thống kê hoạt động theo ngày');
  }
};

/**
 * Lấy thống kê hoạt động theo tuần
 */
export const getWeeklyActivityStats = async (startDate: string, endDate: string): Promise<WeeklyActivityStats[]> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    const response = await axios.get(
      getApiUrl(`/api/activities/stats/weekly?startDate=${startDate}&endDate=${endDate}`), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    // Xử lý cấu trúc response lồng nhau
    const rawData = response.data;
    
    // Trường hợp cấu trúc như mẫu: result -> data -> data (mảng)
    if (rawData && rawData.data && rawData.data.data && Array.isArray(rawData.data.data)) {
      // Ánh xạ dữ liệu vào interface WeeklyActivityStats
      return rawData.data.data.map((week: any) => ({
        weekStartDate: week.startDate || '',
        weekEndDate: week.endDate || '',
        totalActivities: week.totalActivities || 0,
        totalDistance: typeof week.totalDistance === 'number' 
          ? week.totalDistance * 1000 // Chuyển từ km sang m
          : 0,
        totalElevation: week.totalElevation || 0,
        totalDuration: week.totalMovingTime || 0
      }));
    }
    
    // Nếu không khớp với cấu trúc đặc biệt, sử dụng extractArray
    const extractedData = extractArrayFromResponse(response.data, []);
    
    // Ánh xạ dữ liệu vào interface WeeklyActivityStats
    return extractedData.map((week: any) => ({
      weekStartDate: week.startDate || week.weekStartDate || '',
      weekEndDate: week.endDate || week.weekEndDate || '',
      totalActivities: week.totalActivities || 0,
      totalDistance: typeof week.totalDistance === 'number' 
        ? (week.totalDistance < 100 ? week.totalDistance * 1000 : week.totalDistance) // Chuyển từ km sang m nếu < 100
        : 0,
      totalElevation: week.totalElevation || 0,
      totalDuration: week.totalMovingTime || week.totalDuration || 0
    }));
  } catch (error: any) {
    console.error('Lỗi khi lấy thống kê hoạt động theo tuần:', error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể lấy thống kê hoạt động theo tuần');
  }
};

/**
 * Lấy thống kê hoạt động theo tháng
 */
export const getMonthlyActivityStats = async (year: number): Promise<MonthlyActivityStats[]> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    const response = await axios.get(
      getApiUrl(`/api/activities/stats/monthly?year=${year}`), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    // Xử lý cấu trúc response lồng nhau
    const rawData = response.data;
    
    // Trường hợp cấu trúc như mẫu: result -> data -> data (mảng)
    if (rawData && rawData.data && rawData.data.data && Array.isArray(rawData.data.data)) {
      // Ánh xạ dữ liệu vào interface MonthlyActivityStats
      return rawData.data.data.map((month: any) => ({
        month: month.month || month.yearMonth || `${year}-${month.monthNumber || '01'}`,
        totalActivities: month.totalActivities || 0,
        totalDistance: typeof month.totalDistance === 'number' 
          ? (month.totalDistance < 100 ? month.totalDistance * 1000 : month.totalDistance) // Chuyển từ km sang m nếu < 100
          : 0,
        totalElevation: month.totalElevation || 0,
        totalDuration: month.totalMovingTime || month.duration || 0
      }));
    }
    
    // Nếu không khớp với cấu trúc đặc biệt, sử dụng extractArray
    const extractedData = extractArrayFromResponse(response.data, []);
    
    // Ánh xạ dữ liệu vào interface MonthlyActivityStats
    return extractedData.map((month: any) => ({
      month: month.month || month.yearMonth || `${year}-${month.monthNumber || '01'}`,
      totalActivities: month.totalActivities || 0,
      totalDistance: typeof month.totalDistance === 'number' 
        ? (month.totalDistance < 100 ? month.totalDistance * 1000 : month.totalDistance) // Chuyển từ km sang m nếu < 100
        : 0,
      totalElevation: month.totalElevation || 0,
      totalDuration: month.totalMovingTime || month.duration || 0
    }));
  } catch (error: any) {
    console.error('Lỗi khi lấy thống kê hoạt động theo tháng:', error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể lấy thống kê hoạt động theo tháng');
  }
};

/**
 * Lấy bảng xếp hạng theo tournament
 * @param tournamentId ID của giải đấu
 * @param timeframe Khoảng thời gian ('week', 'month', 'year', 'all')
 */
export const getTournamentLeaderboard = async (tournamentId: number, timeframe: string = 'all'): Promise<LeaderboardEntry[]> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    const response = await axios.get(
      getApiUrl(`/api/activities/leaderboard/tournament/${tournamentId}?timeframe=${timeframe}`), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    // Xử lý cấu trúc response đặc biệt cho leaderboard
    const rawData = response.data;
    
    // Trường hợp cấu trúc: result -> data -> leaderboard (mảng)
    if (rawData && rawData.result === 'SUCCESS' && rawData.data && Array.isArray(rawData.data.leaderboard)) {
      // Chuyển đổi từ cấu trúc API sang LeaderboardEntry
      return rawData.data.leaderboard.map((entry: any) => ({
        rank: entry.rank || 0,
        id: entry.id || 0, // API không trả về ID, sử dụng 0 làm giá trị mặc định
        name: entry.memberName || `${entry.firstname || ''} ${entry.lastname || ''}`.trim(),
        totalDistance: typeof entry.totalDistance === 'number' 
          ? entry.totalDistance * 1000 // Chuyển từ km sang m
          : 0,
        totalActivities: entry.totalActivities || 0,
        totalElevation: entry.totalElevation || 0,
        totalDuration: entry.totalMovingTime || 0,
        avatarUrl: entry.avatarUrl || undefined,
        // Thêm các thông tin bổ sung
        teams: entry.teams || '',
        activitiesByType: entry.activitiesByType || {},
        firstname: entry.firstname || '',
        lastname: entry.lastname || '',
        totalDistanceFormatted: entry.totalDistanceFormatted || '',
        totalMovingTimeFormatted: entry.totalMovingTimeFormatted || ''
      }));
    }
    
    return extractArrayFromResponse(response.data, []) as LeaderboardEntry[];
  } catch (error: any) {
    console.error(`Lỗi khi lấy bảng xếp hạng cho giải đấu ${tournamentId}:`, error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể lấy bảng xếp hạng');
  }
};

/**
 * Lấy bảng xếp hạng theo team
 * @param teamId ID của team
 * @param timeframe Khoảng thời gian ('week', 'month', 'year', 'all')
 */
export const getTeamLeaderboard = async (teamId: number, timeframe: string = 'all'): Promise<LeaderboardEntry[]> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    const response = await axios.get(
      getApiUrl(`/api/activities/leaderboard/team/${teamId}?timeframe=${timeframe}`), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    // Xử lý cấu trúc response đặc biệt cho leaderboard
    const rawData = response.data;
    
    // Trường hợp cấu trúc: result -> data -> leaderboard (mảng)
    if (rawData && rawData.result === 'SUCCESS' && rawData.data && Array.isArray(rawData.data.leaderboard)) {
      // Chuyển đổi từ cấu trúc API sang LeaderboardEntry
      return rawData.data.leaderboard.map((entry: any) => ({
        rank: entry.rank || 0,
        id: entry.id || 0, // API không trả về ID, sử dụng 0 làm giá trị mặc định
        name: entry.memberName || `${entry.firstname || ''} ${entry.lastname || ''}`.trim(),
        totalDistance: typeof entry.totalDistance === 'number' 
          ? entry.totalDistance * 1000 // Chuyển từ km sang m
          : 0,
        totalActivities: entry.totalActivities || 0,
        totalElevation: entry.totalElevation || 0,
        totalDuration: entry.totalMovingTime || 0,
        avatarUrl: entry.avatarUrl || undefined,
        // Thêm các thông tin bổ sung
        teams: entry.teams || '',
        activitiesByType: entry.activitiesByType || {},
        firstname: entry.firstname || '',
        lastname: entry.lastname || '',
        totalDistanceFormatted: entry.totalDistanceFormatted || '',
        totalMovingTimeFormatted: entry.totalMovingTimeFormatted || ''
      }));
    }
    
    return extractArrayFromResponse(response.data, []) as LeaderboardEntry[];
  } catch (error: any) {
    console.error(`Lỗi khi lấy bảng xếp hạng cho team ${teamId}:`, error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể lấy bảng xếp hạng');
  }
};

/**
 * Lấy bảng xếp hạng cá nhân
 * @param timeframe Khoảng thời gian ('week', 'month', 'year', 'all')
 */
export const getMembersLeaderboard = async (timeframe: string = 'all'): Promise<LeaderboardEntry[]> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    const response = await axios.get(
      getApiUrl(`/api/activities/leaderboard/members?timeframe=${timeframe}`), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    // Xử lý cấu trúc response đặc biệt cho leaderboard
    const rawData = response.data;
    
    // Trường hợp cấu trúc: result -> data -> leaderboard (mảng)
    if (rawData && rawData.result === 'SUCCESS' && rawData.data && Array.isArray(rawData.data.leaderboard)) {
      // Chuyển đổi từ cấu trúc API sang LeaderboardEntry
      return rawData.data.leaderboard.map((entry: any) => ({
        rank: entry.rank || 0,
        id: entry.id || 0, // API không trả về ID, sử dụng 0 làm giá trị mặc định
        name: entry.memberName || `${entry.firstname || ''} ${entry.lastname || ''}`.trim(),
        totalDistance: typeof entry.totalDistance === 'number' 
          ? entry.totalDistance * 1000 // Chuyển từ km sang m
          : 0,
        totalActivities: entry.totalActivities || 0,
        totalElevation: entry.totalElevation || 0,
        totalDuration: entry.totalMovingTime || 0,
        avatarUrl: entry.avatarUrl || undefined,
        // Thêm các thông tin bổ sung
        teams: entry.teams || '',
        activitiesByType: entry.activitiesByType || {},
        firstname: entry.firstname || '',
        lastname: entry.lastname || '',
        totalDistanceFormatted: entry.totalDistanceFormatted || '',
        totalMovingTimeFormatted: entry.totalMovingTimeFormatted || ''
      }));
    }
    
    // Nếu không khớp với cấu trúc đặc biệt, thử các phương pháp khác
    const extractedData = extractArrayFromResponse(response.data, []);
    
    return extractedData.map((entry: any) => ({
      rank: entry.rank || 0,
      id: entry.id || 0,
      name: entry.name || entry.memberName || `${entry.firstname || ''} ${entry.lastname || ''}`.trim(),
      totalDistance: typeof entry.totalDistance === 'number' 
        ? (entry.totalDistance < 100 ? entry.totalDistance * 1000 : entry.totalDistance) // Chuyển từ km sang m
        : 0,
      totalActivities: entry.totalActivities || 0,
      totalElevation: entry.totalElevation || 0,
      totalDuration: entry.totalMovingTime || entry.totalDuration || 0,
      avatarUrl: entry.avatarUrl || undefined
    }));
  } catch (error: any) {
    console.error('Lỗi khi lấy bảng xếp hạng cá nhân:', error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể lấy bảng xếp hạng cá nhân');
  }
};

/**
 * Lấy thông tin người dùng hiện tại
 */
export const getCurrentUser = async (): Promise<any> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    const response = await axios.get(
      getApiUrl('/api/auth/me'), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error: any) {
    console.error('Lỗi khi lấy thông tin người dùng hiện tại:', error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể lấy thông tin người dùng');
  }
};

/**
 * Lấy danh sách tất cả người dùng (chỉ admin)
 */
export const getAllUsers = async (): Promise<AdminUserInfo[]> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    const response = await axios.get(
      getApiUrl('/api/admin/users'), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return extractArrayFromResponse(response.data, []) as AdminUserInfo[];
  } catch (error: any) {
    console.error('Lỗi khi lấy danh sách người dùng:', error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể lấy danh sách người dùng');
  }
};

/**
 * Kiểm tra trạng thái đồng bộ
 */
export const getSyncStatus = async (): Promise<SyncStatus> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    const response = await axios.get(
      getApiUrl('/api/activities/sync/status'), 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    // Xử lý cấu trúc response lồng nhau
    const rawData = response.data;
    
    // Kiểm tra cấu trúc response
    if (rawData && rawData.result === 'SUCCESS' && rawData.data) {
      // Trích xuất thông tin đồng bộ từ cấu trúc lồng nhau
      const syncHistoryData = rawData.data.syncHistory || {};
      const stravaApiData = rawData.data.stravaApi || {};
      
      return {
        lastSyncTime: syncHistoryData.lastSyncTime || null,
        syncInProgress: false, // Giả định là không đang đồng bộ nếu API không trả về trường này
        syncSuccess: rawData.result === 'SUCCESS' && rawData.data.status === 'success',
        errorMessage: rawData.result !== 'SUCCESS' ? rawData.message : null,
        // Thêm thông tin bổ sung
        timeSinceLastSync: syncHistoryData.timeSinceLastSync || null,
        activitiesLast7Days: syncHistoryData.activitiesLast7Days || 0,
        stravaConnected: stravaApiData.hasValidToken || false,
        stravaTokenExpiresIn: stravaApiData.tokenExpiresInFormatted || null
      };
    }
    
    // Nếu cấu trúc response không khớp với mẫu, trả về trực tiếp
    return {
      lastSyncTime: null,
      syncInProgress: false,
      syncSuccess: rawData && rawData.result === 'SUCCESS',
      errorMessage: rawData && rawData.result !== 'SUCCESS' ? rawData.message : null,
      timeSinceLastSync: null,
      activitiesLast7Days: 0,
      stravaConnected: false,
      stravaTokenExpiresIn: null
    };
  } catch (error: any) {
    console.error('Lỗi khi kiểm tra trạng thái đồng bộ:', error);
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwt_token');
      throw new Error('Token xác thực không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    throw new Error(error.message || 'Không thể kiểm tra trạng thái đồng bộ');
  }
}; 